import { NetworkStatus } from '@cbridge-utils/network';
import { INode } from '@cbridge-packages/node';
import { IStream } from '@cbridge-packages/stream';
import { IClient, ITradingClientSettings, IPricingClientSettings } from '@cbridge-packages/client';
import { ApiAutoSync, IApiServerSettings, IApiTradingConnections } from '@cbridge-packages/api';

export interface IServerStreamSettings {
  tradingGroups?: string;
  securities?: string;
  symbols?: string;
  useSymbolsConjunction?: boolean;
  rejectClientRequestIfOffline?: boolean;
}

export interface IServerStream {
  serverId: IServer['serverId'];
  streamId: IStream['streamId'];
  streamName: string;
  tradingEnabled: boolean;
  pricingEnabled: boolean;
  clientSymbolsCount?: number;
  settings?: IServerStreamSettings;
}

export interface IServerStreamForm {
  serverId?: IServer['serverId'];
  streamId?: IStream['streamId'];
  tradingEnabled?: boolean;
  pricingEnabled?: boolean;
  settings?: IServerStreamSettings;
  json?: string;
}

export enum ServerType {
  MT4 = 'MT4',
  MT5 = 'MT5',
  cTrader = 'cTrader',
  FixTaker = 'FIX_TAKER',
}

export enum ConnectionStatus {
  Online = 'Online',
  Offline = 'Offline',
  Na = 'Na',
  Disabled = 'Disabled',
  Unknown = 'Unknown',
}

/**
 * cTrader Manager connection used for risk reporting (LB-4732); cTrader servers only. plant/environment: 1-32 chars
 */
export interface ICTraderManagerSettings {
  plant: string;
  environment: string;
  managerLogin: string;
  managerPassword: string;
}

/**
 * Details shape: BE derives host/port from plant/environment. Absent right after a local update until the next load
 */
export interface ICTraderManagerSettingsDetails extends ICTraderManagerSettings {
  host?: string;
  port?: number;
}

export interface IServerBase {
  serverId: string;
  serverName: string;
  nodeId: INode['nodeId'];
  nodeName: string;
  type: ServerType;
  tradingConnections?: IApiTradingConnections;
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
  /**
   * Independent markout (risk reporting) on this server; details only, the list has no such field (LB-4701, cTrader: LB-4732)
   */
  riskReportingEnabled?: boolean;
  /**
   * cTrader only, required while risk reporting is on (LB-4732)
   */
  cTraderManagerSettings?: ICTraderManagerSettings;
}

/**
 * Form shape of the cTrader Manager block: optional keys like the other nested form objects (yup makes them
 * optional without strictNullChecks), required by the schema while the block is visible
 */
export type ICTraderManagerSettingsForm = Partial<ICTraderManagerSettings>;

export interface IServerForm extends Omit<IServerBase, 'tradingConnections' | 'throttlingInterval' | 'cTraderManagerSettings'> {
  /**
   * in milliseconds; null = cleared in the form, the API request then omits the field
   */
  throttlingInterval?: number | null;
  cTraderManagerSettings?: ICTraderManagerSettingsForm;
}

export interface IServer extends IServerBase {
  streamsCount?: number;
  clientSymbolCount?: number;

  // that field has a list of api response but hasn't in details response
  autosync?: ApiAutoSync;
  connectionStatus?: ConnectionStatus;
  pricingConnectionStatus?: ConnectionStatus;
  /**
   * Independent markout (risk reporting) client status; list only, with the riskManagement feature (LB-5266)
   */
  reportingConnectionStatus?: ConnectionStatus;
}

export type IServerSettings = IApiServerSettings;

export interface IServerDetails extends IServer {
  pricingClient: IPricingClient;
  settings?: IServerSettings;
  cTraderManagerSettings?: ICTraderManagerSettingsDetails;
  defaultSettings?: {
    pricingSession: IPricingClientSettings;
    tradingSession: ITradingClientSettings;
  };
  tradingClients: ITradingClient[];
  streams: IServerStream[];
}

export interface IConnectedClient {
  clientId: IClient['clientId'];
  clientSymbolsCount: number;
  enabled: boolean;
  serverId: IServer['serverId'];
}

export interface IPricingClient extends IConnectedClient {
  settings?: IPricingClientSettings;
}

export interface IPricingClientForm {
  settings?: IPricingClientSettings;
  json?: string;
}

export interface ITradingClient extends IConnectedClient {
  nodeId: IServer['nodeId'];
  status?: ConnectionStatus;
}

export interface ITradingClientForm {
  clientId?: IClient['clientId'];
  serverId?: IServer['serverId'];
  streamId?: IStream['streamId'];
  enabled?: boolean;
  settings?: ITradingClientSettings;
  json?: string;
}

export interface IServerListKey {
  nodeId?: INode['nodeId'];
}

export interface IServerStoreData extends IServerDetails {
  networkStatus?: NetworkStatus;
}

export interface IServerStore {
  data: Record<IServer['serverId'], IServerStoreData>;
  listNetworkStatus: NetworkStatus;
}

export interface IServerStoreSegment {
  server: IServerStore;
}
